import { DEFAULT_RULES_THRESHOLDS } from '~/rules/thresholds'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type ValidationIssue, type ValidationIssues } from './common'

/**
 * The weekly recovery rating (data-model.md, D-066).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  EVERY ITEM IS PHRASED SO THAT 5 IS THE BETTER OUTCOME.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * This is the whole reason the item keys read the way they do. The handoff asks
 * for "simple scales such as 1 to 5" without ever saying which end is good, and
 * for two of the five the plain reading is inverted: high hunger and high stress
 * are bad, where high energy and high sleep quality are good. A single threshold
 * — D-066's "any single item at or below 2" — cannot apply across five items
 * pointing in two directions.
 *
 * So the questions are asked as `hungerControl` and `calm` rather than as
 * hunger and stress. The direction is fixed in the vocabulary rather than
 * corrected by the rules that read it, because a rule that has to remember to
 * invert two of five inputs will eventually forget.
 *
 * `scaleVersion` is stored on every check for the same reason D-046 stores a
 * phase snapshot: a later rewording must not silently change what a stored 3
 * meant. Rewording an item, changing the range, or changing the direction of any
 * item is a version bump, never an edit in place.
 */
export const RECOVERY_ITEMS = [
  'hungerControl',
  'energy',
  'sleepQuality',
  'calm',
  'trainingPerformance',
] as const

export type RecoveryItem = (typeof RECOVERY_ITEMS)[number]

/**
 * Recorded when offered, and deliberately **not** part of the five-item mean.
 *
 * D-066's recovery threshold is defined over five items. Folding a sixth or
 * seventh into that mean would move a threshold the user approved against a
 * different denominator, which is a retune disguised as a data-model choice.
 */
export const OPTIONAL_RECOVERY_ITEMS = ['motivation', 'sorenessRecovery'] as const

export type OptionalRecoveryItem = (typeof OPTIONAL_RECOVERY_ITEMS)[number]

/**
 * Bounds come from `rules/thresholds.ts` rather than being restated here, so the
 * shape the app stores and the range the rules judge cannot drift apart.
 */
const SCALE_MIN = DEFAULT_RULES_THRESHOLDS.recovery.scaleMin
const SCALE_MAX = DEFAULT_RULES_THRESHOLDS.recovery.scaleMax

/** Bumped when any item is reworded, re-ranged, or reversed. Never edited. */
export const RECOVERY_SCALE_VERSION = 1

export interface RecoveryCheck {
  /** All five required items. A partial recovery check is not representable. */
  readonly ratings: Readonly<Record<RecoveryItem, number>>
  readonly optionalRatings?: Readonly<Partial<Record<OptionalRecoveryItem, number>>>
  readonly scaleVersion: number
  readonly notes?: string
}

export interface RecoveryCheckInput {
  readonly ratings: Readonly<Partial<Record<string, number>>>
  readonly optionalRatings?: Readonly<Partial<Record<string, number>>>
  readonly notes?: string
}

/**
 * The question text, and the answer each end of the scale means.
 *
 * Presentation only — never persisted, never compared against stored data. The
 * `low`/`high` wording is here rather than in a component because it is what
 * makes the "5 is better" contract visible to the person answering; a screen
 * that rendered a bare 1–5 row would leave the direction to be guessed.
 */
export const RECOVERY_ITEM_PROMPTS: Readonly<
  Record<RecoveryItem, { readonly question: string; readonly low: string; readonly high: string }>
> = {
  hungerControl: {
    question: 'Hunger control',
    low: 'Hungry most of the time',
    high: 'Comfortable between meals',
  },
  energy: {
    question: 'Energy',
    low: 'Drained most days',
    high: 'Good energy most days',
  },
  sleepQuality: {
    question: 'Sleep quality',
    low: 'Slept badly most nights',
    high: 'Slept well most nights',
  },
  calm: {
    question: 'Calm',
    low: 'Very stressed',
    high: 'Settled and calm',
  },
  trainingPerformance: {
    question: 'Training performance',
    low: 'Sessions felt much harder',
    high: 'Sessions felt strong',
  },
}

export const OPTIONAL_RECOVERY_ITEM_PROMPTS: Readonly<
  Record<
    OptionalRecoveryItem,
    { readonly question: string; readonly low: string; readonly high: string }
  >
> = {
  motivation: {
    question: 'Motivation',
    low: 'Hard to get started',
    high: 'Keen to train and stay on plan',
  },
  sorenessRecovery: {
    question: 'Recovery between sessions',
    low: 'Still sore when the next session came',
    high: 'Recovered well between sessions',
  },
}

export function isRecoveryItem(value: string): value is RecoveryItem {
  return (RECOVERY_ITEMS as readonly string[]).includes(value)
}

export function isOptionalRecoveryItem(value: string): value is OptionalRecoveryItem {
  return (OPTIONAL_RECOVERY_ITEMS as readonly string[]).includes(value)
}

function validateRating(field: string, value: number | undefined): ValidationIssue | undefined {
  if (value === undefined) {
    return issue(field, 'Give a rating from 1 to 5.')
  }
  if (!Number.isInteger(value) || value < SCALE_MIN || value > SCALE_MAX) {
    return issue(field, `Ratings are whole numbers from ${SCALE_MIN} to ${SCALE_MAX}.`)
  }
  return undefined
}

export function validateRecoveryCheck(input: RecoveryCheckInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  for (const item of RECOVERY_ITEMS) {
    const problem = validateRating(item, input.ratings[item])
    if (problem !== undefined) issues.push(problem)
  }

  for (const [key, value] of Object.entries(input.optionalRatings ?? {})) {
    if (!isOptionalRecoveryItem(key)) {
      issues.push(issue('optionalRatings', `Unknown recovery item: ${key}.`))
      continue
    }
    // An optional item that was skipped is absent, not zero.
    if (value === undefined) continue
    const problem = validateRating(key, value)
    if (problem !== undefined) issues.push(problem)
  }

  for (const key of Object.keys(input.ratings)) {
    if (!isRecoveryItem(key)) {
      issues.push(issue('ratings', `Unknown recovery item: ${key}.`))
    }
  }

  return issues
}

/** Build a validated recovery check. Pure — no clock, no storage. */
export function buildRecoveryCheck(
  input: RecoveryCheckInput,
): Result<RecoveryCheck, ValidationIssues> {
  const issues = validateRecoveryCheck(input)
  if (issues.length > 0) return err(issues)

  const ratings: Record<string, number> = {}
  for (const item of RECOVERY_ITEMS) {
    // Validation above guarantees each is present and in range.
    ratings[item] = input.ratings[item] as number
  }

  const optional: Record<string, number> = {}
  for (const item of OPTIONAL_RECOVERY_ITEMS) {
    const value = input.optionalRatings?.[item]
    if (value !== undefined) optional[item] = value
  }

  return ok({
    ratings: ratings as Record<RecoveryItem, number>,
    ...(Object.keys(optional).length === 0
      ? {}
      : { optionalRatings: optional as Partial<Record<OptionalRecoveryItem, number>> }),
    scaleVersion: RECOVERY_SCALE_VERSION,
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  })
}
